import type { PolicyMappingVerificationStatus } from "./enums";

export type MappingTreeStatus = "unmapped" | "unapproved" | "approved" | "mixed";

export type MappingTreeSection<SectionId extends string = string> = {
  readonly id: SectionId;
  readonly parentId: SectionId | null;
};

export type MappingTreeMapping<SectionId extends string = string> = {
  readonly sectionId: SectionId;
  readonly verificationStatus: PolicyMappingVerificationStatus;
};

export type MappingTreeSummary = {
  readonly status: MappingTreeStatus;
  readonly mappingCount: number;
  readonly approvedCount: number;
  readonly unapprovedCount: number;
};

const emptySummary = (): MappingTreeSummary => ({
  status: "unmapped",
  mappingCount: 0,
  approvedCount: 0,
  unapprovedCount: 0,
});

export const mappingTreeStatusFromCounts = (
  approvedCount: number,
  unapprovedCount: number,
): MappingTreeStatus => {
  if (approvedCount > 0 && unapprovedCount > 0) return "mixed";
  if (approvedCount > 0) return "approved";
  if (unapprovedCount > 0) return "unapproved";
  return "unmapped";
};

/**
 * Roll direct leaf mappings up through every ancestor. Any mapped descendant
 * marks its whole branch as mapped, while approval remains visible as green,
 * red, or a split red/green state.
 */
export const mappingTreeSummaries = <SectionId extends string>(
  sections: readonly MappingTreeSection<SectionId>[],
  mappings: readonly MappingTreeMapping<SectionId>[],
): Map<SectionId, MappingTreeSummary> => {
  const knownIds = new Set(sections.map((section) => section.id));
  const childrenByParent = new Map<SectionId, SectionId[]>();
  for (const section of sections) {
    if (section.parentId === null || !knownIds.has(section.parentId)) continue;
    const children = childrenByParent.get(section.parentId) ?? [];
    childrenByParent.set(section.parentId, [...children, section.id]);
  }

  const direct = new Map<SectionId, { approvedCount: number; unapprovedCount: number }>();
  for (const mapping of mappings) {
    if (!knownIds.has(mapping.sectionId)) continue;
    const counts = direct.get(mapping.sectionId) ?? { approvedCount: 0, unapprovedCount: 0 };
    if (mapping.verificationStatus === "humanVerified") counts.approvedCount += 1;
    else counts.unapprovedCount += 1;
    direct.set(mapping.sectionId, counts);
  }

  const summaries = new Map<SectionId, MappingTreeSummary>();
  const visiting = new Set<SectionId>();
  const summarize = (sectionId: SectionId): MappingTreeSummary => {
    const cached = summaries.get(sectionId);
    if (cached !== undefined) return cached;
    if (visiting.has(sectionId)) return emptySummary();
    visiting.add(sectionId);

    const own = direct.get(sectionId) ?? { approvedCount: 0, unapprovedCount: 0 };
    let approvedCount = own.approvedCount;
    let unapprovedCount = own.unapprovedCount;
    for (const childId of childrenByParent.get(sectionId) ?? []) {
      const child = summarize(childId);
      approvedCount += child.approvedCount;
      unapprovedCount += child.unapprovedCount;
    }

    visiting.delete(sectionId);
    const summary = {
      status: mappingTreeStatusFromCounts(approvedCount, unapprovedCount),
      mappingCount: approvedCount + unapprovedCount,
      approvedCount,
      unapprovedCount,
    };
    summaries.set(sectionId, summary);
    return summary;
  };

  for (const section of sections) summarize(section.id);
  return summaries;
};

export const mappingTreeDescendantIds = <SectionId extends string>(
  sections: readonly MappingTreeSection<SectionId>[],
  ancestorId: SectionId,
): Set<SectionId> => {
  const childrenByParent = new Map<SectionId, SectionId[]>();
  for (const section of sections) {
    if (section.parentId === null) continue;
    const children = childrenByParent.get(section.parentId) ?? [];
    childrenByParent.set(section.parentId, [...children, section.id]);
  }

  const descendants = new Set<SectionId>([ancestorId]);
  const pending = [ancestorId];
  while (pending.length > 0) {
    const parentId = pending.pop();
    if (parentId === undefined) break;
    for (const childId of childrenByParent.get(parentId) ?? []) {
      if (descendants.has(childId)) continue;
      descendants.add(childId);
      pending.push(childId);
    }
  }
  return descendants;
};


export type UnresolvedMappingNavigation<SectionId> = {
  readonly previous: SectionId | null;
  readonly next: SectionId | null;
};

export const unresolvedMappingNavigation = <SectionId>({
  unresolvedIds,
  currentId,
}: {
  readonly unresolvedIds: readonly SectionId[];
  readonly currentId: SectionId | null;
}): UnresolvedMappingNavigation<SectionId> => {
  const index = currentId === null ? -1 : unresolvedIds.indexOf(currentId);
  return {
    previous: index > 0 ? unresolvedIds[index - 1] ?? null : null,
    next: index >= 0 && index < unresolvedIds.length - 1
      ? unresolvedIds[index + 1] ?? null
      : index === -1 ? unresolvedIds[0] ?? null : null,
  };
};
